import { getAuthContext } from './authContext'
import { invalidateAuthCache } from './authValidationCache'
import { invalidateAdminAuthCache } from '../utils/adminAuthCache'

const SESSION_PREFERENCE_KEYS = ['app_local_theme', 'app_locale'] as const

export const isLoginRequest = (url?: string) => {
  if (!url) return false
  return url.includes('/admins/login') || url.includes('/login')
}

/**
 * Returns the active token.
 * Priority order:
 * 1. impersonation_token from sessionStorage (impersonation sessions)
 * 2. admin_token from sessionStorage (agent direct login)
 * 3. admin_token from localStorage (admin login)
 */
export const getActiveToken = (): string | null => {
  const impersonationToken = sessionStorage.getItem('impersonation_token')
  if (impersonationToken) {
    return impersonationToken
  }

  const sessionToken = sessionStorage.getItem('admin_token')
  if (sessionToken) {
    return sessionToken
  }

  return localStorage.getItem('admin_token')
}

const clearSessionPreferences = () => {
  SESSION_PREFERENCE_KEYS.forEach((key) => sessionStorage.removeItem(key))
}

export const clearAuthSession = () => {
  invalidateAuthCache()
  invalidateAdminAuthCache()

  const { isImpersonating, isAgentDirectLogin } = getAuthContext()

  if (isImpersonating) {
    sessionStorage.removeItem('impersonation_session')
    sessionStorage.removeItem('impersonation_token')
    clearSessionPreferences()
    return
  }

  if (isAgentDirectLogin) {
    sessionStorage.removeItem('admin_token')
    sessionStorage.removeItem('role')
    clearSessionPreferences()
    return
  }

  localStorage.removeItem('admin_token')
  localStorage.removeItem('role')
  localStorage.removeItem('user')
  localStorage.removeItem('impersonation_was_used')
}

export const forceLogout = () => {
  const { isImpersonating } = getAuthContext()

  if (isImpersonating) {
    clearAuthSession()
    window.location.replace('/auth/agent-logout')
    return
  }

  clearAuthSession()
  window.location.replace('/auth/login')
}

export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (!payload?.exp) return false
    return Date.now() >= payload.exp * 1000
  } catch {
    return false
  }
}
